import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Check, AlertCircle } from 'lucide-react';
import { useAuth } from '../App';
import axios from 'axios';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const HandleSetupModal = ({ open, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [handle, setHandle] = useState('');
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isAvailable, setIsAvailable] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  const validateHandle = (value) => {
    if (!value) return null;
    if (value.length < 3) return 'Handle must be at least 3 characters';
    if (value.length > 30) return 'Handle must be less than 30 characters';
    if (!/^[a-zA-Z0-9_]+$/.test(value)) return 'Handle can only contain letters, numbers, and underscores';
    return null;
  };

  const checkAvailability = async (handleValue) => {
    if (!handleValue || validateHandle(handleValue)) return;
    
    setChecking(true);
    try {
      const response = await axios.get(`${API}/creator/handle-check`, {
        params: { handle: handleValue.toLowerCase() }
      });
      setIsAvailable(response.data.available);
    } catch (error) {
      console.error('Error checking handle availability:', error);
      setIsAvailable(null);
    } finally {
      setChecking(false);
    }
  };

  const handleInputChange = (value) => {
    const cleanValue = value.toLowerCase().replace(/[^a-zA-Z0-9_]/g, '');
    setHandle(cleanValue);
    setIsAvailable(null);
    
    // Debounce availability check
    if (cleanValue.length >= 3) {
      setTimeout(() => {
        if (cleanValue === handle.toLowerCase()) {
          checkAvailability(cleanValue);
        }
      }, 500);
    }
  };

  const handleSave = async () => {
    const validation = validateHandle(handle);
    if (validation) {
      toast.error(validation);
      return;
    }

    if (isAvailable === false) {
      toast.error('This handle is already taken');
      return;
    }

    setSaving(true);
    try {
      // Save handle to profile
      const response = await axios.put(`${API}/creator/profile`, {
        handle: handle.toLowerCase()
      });
      
      // Verify authentication token is still valid
      try {
        await axios.get(`${API}/creators/me/profile`);
      } catch (authError) {
        if (authError.response?.status === 401) {
          toast.error('Session expired. Please log in again to continue.');
          onClose();
          // Redirect to login with return URL
          window.location.href = '/auth?redirect=' + encodeURIComponent(`/creator/${handle.toLowerCase()}`);
          return;
        }
      }
      
      toast.success('✅ Your Trap Comix profile is live! You can customize it anytime.');
      
      // Call success callback  
      onSuccess(handle.toLowerCase());
      
      // Set redirecting state for UI feedback
      setRedirecting(true);
      
      // Determine profile type and redirect URL
      const isCreator = user && ['artist', 'author'].includes(user.role);
      const profileUrl = isCreator 
        ? `/creator/${handle.toLowerCase()}?from=setup`
        : `/user/${handle.toLowerCase()}?from=setup`;
      
      // Redirect to live profile after a brief delay for UX
      setTimeout(() => {
        window.location.href = profileUrl;
      }, 1500);
      
    } catch (error) {
      console.error('Error setting handle:', error);
      
      if (error.response?.status === 401) {
        // Retry authentication once
        try {
          await axios.get(`${API}/creators/me/profile`);
          // If successful, retry the handle save
          await handleSave();
          return;
        } catch (retryError) {
          toast.error('Session expired. Please log in again to continue.');
          onClose();
          window.location.href = '/auth';
          return;
        }
      }
      
      const message = error.response?.data?.detail || 'Failed to set handle';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const validation = validateHandle(handle);
  const canSave = handle.length >= 3 && !validation && isAvailable === true;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Set Your Public Handle</DialogTitle>
          <DialogDescription className="text-gray-400">
            Choose a unique handle to publish your public profile. This will be your creator URL.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor="handle">Your Handle</Label>
            <div className="relative mt-2">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-400 text-sm">@</span>
              </div>
              <Input
                id="handle"
                value={handle}
                onChange={(e) => handleInputChange(e.target.value)}
                className="bg-gray-800 border-gray-600 text-white pl-8"
                placeholder="yourhandle"
                maxLength={30}
              />
              
              {/* Availability Indicator */}
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                {checking && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                )}
                {!checking && handle.length >= 3 && !validation && isAvailable === true && (
                  <Check className="w-4 h-4 text-green-500" />
                )}
                {!checking && handle.length >= 3 && !validation && isAvailable === false && (
                  <AlertCircle className="w-4 h-4 text-red-500" />
                )}
              </div>
            </div>
            
            {/* Validation Messages */}
            {validation && (
              <div className="text-red-400 text-sm mt-1">{validation}</div>
            )}
            {!validation && handle.length >= 3 && isAvailable === false && (
              <div className="text-red-400 text-sm mt-1">This handle is already taken</div>
            )}
            {!validation && handle.length >= 3 && isAvailable === true && (
              <div className="text-green-400 text-sm mt-1">Handle is available!</div>
            )}
          </div>

          {/* Preview */}
          {handle && !validation && (
            <div className="bg-gray-800 p-3 rounded-lg">
              <div className="text-xs text-gray-400 mb-1">Your profile URL will be:</div>
              <div className="text-orange-400 font-mono text-sm">
                trapcomix.com/creator/{handle.toLowerCase()}
              </div>
            </div>
          )}
        </div>

{redirecting ? (
          <div className="mt-6 text-center">
            <div className="mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
            </div>
            <div className="space-y-2">
              <div className="text-white font-medium">Setting up your profile...</div>
              <div className="text-gray-400 text-sm">Redirecting to your live Trap Comix profile</div>
            </div>
            <div className="mt-4 bg-green-600/20 border border-green-600/30 rounded-lg p-3">
              <div className="text-green-400 text-sm flex items-center justify-center">
                <Check className="w-4 h-4 mr-2" />
                Your Trap Comix profile is live!
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-end space-x-2 mt-6">
            <Button 
              variant="outline" 
              onClick={onClose}
              disabled={saving}
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={!canSave || saving}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save & Continue'
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HandleSetupModal;